import BaseResource                from '@/lib/data/resource/BaseResource'
import { collect }    from '@/lib/utils/array'
import NIISummaryPieAssetResource from '@/api/resources/risks/NIISummaryPieAssetResource'
import i18n                        from '@/lang/lang'
import { clone }                   from '@/lib/utils/helper'
import NIISummaryAssetResource from './NIISummaryAssetResource'
import dayjs from 'dayjs'

export default class NIISummaryResource extends BaseResource {
  constructor (payload = {}) {
    super(payload)

    const p = payload ?? {}
    this.selectedPeriod = payload.selectedPeriod ?? 'risk12mAnalysisData'

    // Access the data from the correct period key
    const o = p[this.selectedPeriod] ?? {}
    const summary = o.summary ?? {}

    this.BaseCurrency = summary.baseCurrency ?? {}
    this.Assets = summary.components ?? []
    this.Items = collect(summary.items ?? [], NIISummaryAssetResource)
    this.PieAssets = collect(summary.pieComponents ?? [], NIISummaryPieAssetResource)
    this.InternalThresholds = o.internalThresholds ?? []
    this.AnalysisName = p.analysisName ?? ''
    this.ValueDate = p.valueDate ?? ''
    this.ValueDateFmt = this.getDateFmt(this.ValueDate)
    this.RiskSelectionVisible = p.riskSelectionVisible ?? false
  }

  get NIIBaseCurrency () {
    return this.BaseCurrency ?? {}
  }

  get TotalRiskPerAsset () {
    return this.Items.filter(item => item.Asset !== i18n.t('Common.Asset.NetInterestIncome'))
  }

  get Portfolio () {
    const index = this.Items.findIndex(item => item.Asset === i18n.t('Common.Asset.NetInterestIncome'))
    if (index > -1) {
      return this.Items[index]
    }

    return null
  }

  get PortfolioContributionValues () {
    const p = this.Portfolio

    if (p) {
      return [
        p?.ExtremeEventRaw,
        p?.SimulationRaw,
        p?.HistoricalRaw
      ]
    }
    return []
  }

  get AnnualRisk () {
    return this.Portfolio?.AnnualRisk || ''
  }

  get PieValues () {
    if (this.PieAssets) {
      return this.PieAssets.map(item => {
        return {
          name : item.Asset,
          type : item.Type,
          value: item.Value
        }
      })
    }

    return []
  }

  get MethodologyContribution () {
    const series = [{
      xAxis    : i18n.t('Risks.Section.RiskSummary.MethodologyBreakdown.BankingBarChart.XAxis'),
      data     : [],
      portfolio: this.PortfolioContributionValues
    }]

    const v = this.Items

    if (v && v.length) {
      const o = clone(v).filter(item => item.Asset !== i18n.t('Common.Asset.NetInterestIncome'))
      const data = []
      const dataAggregate = []
      const dataUp = {
        A: [],
        B: [],
        C: []
      }
      const dataDown = {
        A: [],
        B: [],
        C: []
      }
      o.forEach(item => {
        data[item.Asset] = []
        dataAggregate[item.Asset] = item.PerCategoryItems
      })
      o.forEach(item => {
        // Extreme Events
        if (item.hasOwnProperty('ExtremeEventRaw')) {
          data[item.Asset].push(item.ExtremeEventRaw)

          if (item.ExtremeEventRaw > 0) {
            dataUp.A.push(item.Asset)
          } else {
            dataDown.A.push(item.Asset)
          }
        }

        // Forward Simulation
        if (item.hasOwnProperty('SimulationRaw')) {
          data[item.Asset].push(item.SimulationRaw)

          if (item.SimulationRaw > 0) {
            dataUp.B.push(item.Asset)
          } else {
            dataDown.B.push(item.Asset)
          }
        }

        // Historical
        if (item.hasOwnProperty('HistoricalRaw')) {
          data[item.Asset].push(item.HistoricalRaw)

          if (item.HistoricalRaw > 0) {
            dataUp.C.push(item.Asset)
          } else {
            dataDown.C.push(item.Asset)
          }
        }
      })

      series[0].data = data
      series[0].dataAggregate = dataAggregate
    }

    return series
  }

  /* METHODS */
  getDateFmt (date) {
    return date ? dayjs(date).format('MMMM DD, YYYY') : ''
  }
}
